// oxlint-disable sorare/no-unused-fragment-fields
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { catchAll } from '@sorare/routing';
import {
  ANY_SPORT_CARDS_SLUG,
  ANY_SPORT_MARKET_HOME,
  ANY_SPORT_MARKET_SCOUT,
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
  ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS,
  ANY_SPORT_MARKET_WATCHLISTS,
  ANY_SPORT_MY_CLUB_SLUG,
  ANY_SPORT_PLAYERS_SLUG,
  ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD,
  ANY_SPORT_TEAMS_SLUG_CARDS,
  FOOTBALL_BUNDLED_AUCTIONS_ID,
  FOOTBALL_COUNTRIES_COUNTRYCODE_CARDS,
  FOOTBALL_LEAGUES_SLUG_CARDS,
  FOOTBALL_TEAMS_SLUG_CARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import type {
  BidderFragment,
  CardFragment,
  OfferSideFragment,
  PrimaryOfferUpdate,
  PrimaryOfferUpdateFragment,
  PrimaryOfferUpdateVariables,
  SmallUserForSubscription_user,
  TokenAuctionUpdate,
  TokenAuctionUpdateFragment,
  TokenAuctionUpdateVariables,
  TokenOfferUpdate,
  TokenOfferUpdateFragment,
  TokenOfferUpdateVariables,
} from './__generated__/subscriptions.graphql.ts';

export const AUCTION_SUBSCRIPTION_ROUTES = [
  ANY_SPORT_CARDS_SLUG,
  FOOTBALL_BUNDLED_AUCTIONS_ID,
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_HOME,
  catchAll(ANY_SPORT_PLAYERS_SLUG),
  FOOTBALL_COUNTRIES_COUNTRYCODE_CARDS,
  FOOTBALL_LEAGUES_SLUG_CARDS,
  FOOTBALL_TEAMS_SLUG_CARDS,
  ANY_SPORT_TEAMS_SLUG_CARDS,
  catchAll(ANY_SPORT_MARKET_SCOUT),
  catchAll(ANY_SPORT_MARKET_WATCHLISTS),
];

export const OFFERS_SUBSCRIPTION_ROUTES = [
  ANY_SPORT_CARDS_SLUG,
  ANY_SPORT_MARKET_HOME,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
  catchAll(ANY_SPORT_PLAYERS_SLUG),
  FOOTBALL_COUNTRIES_COUNTRYCODE_CARDS,
  FOOTBALL_LEAGUES_SLUG_CARDS,
  FOOTBALL_TEAMS_SLUG_CARDS,
  ANY_SPORT_TEAMS_SLUG_CARDS,
  catchAll(ANY_SPORT_MY_CLUB_SLUG),
  catchAll(ANY_SPORT_MARKET_SCOUT),
  catchAll(ANY_SPORT_MARKET_WATCHLISTS),
];

export const PRIMARY_OFFERS_SUBSCRIPTION_ROUTES = [
  ANY_SPORT_CARDS_SLUG,
  ANY_SPORT_MARKET_HOME,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS,
  catchAll(ANY_SPORT_PLAYERS_SLUG),
  catchAll(ANY_SPORT_MARKET_SCOUT),
  catchAll(ANY_SPORT_MARKET_WATCHLISTS),
  catchAll(ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD),
];

const smallUserFragment = gql`
  fragment SmallUserForSubscription_user on User {
    slug
    suspended
    nickname
    pictureUrl(derivative: "avatar")
    profile {
      id
      badge {
        slug
        pictureUrl
        description
      }
    }
  }
` as TypedDocumentNode<SmallUserForSubscription_user>;
const bidderFragment = gql`
  fragment BidderFragment on BlockchainUser {
    ... on User {
      slug
      status
      ...SmallUserForSubscription_user
    }
    ... on AnonymousUser {
      id
      ethereumAddress
    }
  }
  ${smallUserFragment}
` as TypedDocumentNode<BidderFragment>;

const cardFragment = gql`
  fragment CardFragment on AnyCardInterface {
    slug
    user {
      slug
    }
    tokenOwner {
      id
      from
      blockchain
      address
      amounts {
        ...MonetaryAmountFragment_monetaryAmount
      }
      transferType
      user {
        slug
      }
      account {
        id
        accountable {
          ... on Node {
            id
          }
          ... on EthereumAccount {
            id
            address
          }
          ... on StarkwareAccount {
            id
            starkKey
          }
        }
        owner {
          ... on User {
            slug
          }
          ... on Contract {
            id
          }
        }
      }
    }
  }
  ${monetaryAmountFragment}
` as TypedDocumentNode<CardFragment>;

const tokenAuctionFragment = gql`
  fragment TokenAuctionUpdateFragment on TokenAuction {
    id
    open
    endDate
    currentPrice
    currency
    bidsCount
    minNextBid
    anyCards {
      slug
      ...CardFragment
      latestEnglishAuction {
        id
      }
    }
    bestBid {
      id
      userBidder {
        slug
        ...BidderFragment
      }
    }
    bids(first: 5) {
      nodes {
        id
        amounts {
          ...MonetaryAmountFragment_monetaryAmount
        }
        userBidder {
          slug
          ...BidderFragment
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
  ${cardFragment}
  ${bidderFragment}
  ${monetaryAmountFragment}
` as TypedDocumentNode<TokenAuctionUpdateFragment>;

const offerSideFragment = gql`
  fragment OfferSideFragment on TokenOfferSide {
    id
    amounts {
      ...MonetaryAmountFragment_monetaryAmount
    }
    anyCards {
      ...CardFragment
      slug
      liveSingleSaleOffer {
        id
      }
      myMintedSingleSaleOffer {
        id
      }
    }
  }
  ${monetaryAmountFragment}
  ${cardFragment}
` as TypedDocumentNode<OfferSideFragment>;

const tokenOfferFragment = gql`
  fragment TokenOfferUpdateFragment on TokenOffer {
    id
    endDate
    status
    senderSide {
      id
      ...OfferSideFragment
    }
    receiverSide {
      id
      ...OfferSideFragment
    }
  }
  ${offerSideFragment}
` as TypedDocumentNode<TokenOfferUpdateFragment>;

const primaryOfferUpdateFragment = gql`
  fragment PrimaryOfferUpdateFragment on TokenPrimaryOffer {
    id
    endDate
    status
    buyer {
      slug
      ...SmallUserForSubscription_user
    }
    price {
      ...MonetaryAmountFragment_monetaryAmount
    }
    signedAmount
    instantBuyCampaign {
      id
      endDate
      remainingAtCurrentPrice
      quantityPerStep
      soldOut
    }
  }
  ${monetaryAmountFragment}
  ${smallUserFragment}
` as TypedDocumentNode<PrimaryOfferUpdateFragment>;

export const tokenAuctionSubscription = gql`
  subscription TokenAuctionUpdate($sports: [Sport!]) {
    tokenAuctionWasUpdated(sports: $sports) {
      id
      ...TokenAuctionUpdateFragment
    }
  }
  ${tokenAuctionFragment}
` as TypedDocumentNode<TokenAuctionUpdate, TokenAuctionUpdateVariables>;

export const tokenOfferSubscription = gql`
  subscription TokenOfferUpdate($sports: [Sport!]) {
    tokenOfferWasUpdated(sports: $sports) {
      id
      ...TokenOfferUpdateFragment
    }
  }
  ${tokenOfferFragment}
` as TypedDocumentNode<TokenOfferUpdate, TokenOfferUpdateVariables>;

export const primaryOfferSubscription = gql`
  subscription PrimaryOfferUpdate($sports: [Sport!]) {
    primaryOfferWasUpdated(sports: $sports) {
      id
      ...PrimaryOfferUpdateFragment
    }
  }
  ${primaryOfferUpdateFragment}
` as TypedDocumentNode<PrimaryOfferUpdate, PrimaryOfferUpdateVariables>;
